const PROGRESS_KEY = 'color-dots:save:v1';

export interface LevelProgress {
  stars: number;
  hints: number;
  timeSec: number;
  at: string;
}

export interface SavedProgress {
  version: number;
  completed: Record<string, LevelProgress>;
  lastLevel: string;
}

function emptyProgress(): SavedProgress {
  return { version: 1, completed: {}, lastLevel: 'e-001' };
}

export function loadProgress(): SavedProgress {
  try {
    const raw = localStorage.getItem(PROGRESS_KEY);
    if (!raw) return emptyProgress();
    const parsed = JSON.parse(raw) as Partial<SavedProgress>;
    return {
      version: 1,
      completed: parsed.completed ?? {},
      lastLevel: typeof parsed.lastLevel === 'string' ? parsed.lastLevel : 'e-001',
    };
  } catch {
    return emptyProgress();
  }
}

export function saveProgress(progress: SavedProgress): void {
  localStorage.setItem(PROGRESS_KEY, JSON.stringify(progress));
}

/**
 * "Par" time for the 3rd star: a rough not-stuck pace, scaled by board area and
 * pair count so bigger/harder levels get proportionally more time. First-pass
 * tuning (2026-09-15) was way too generous (90-230s) — real play data from the
 * user showed clears in 11-52s on easy boards, and their own baseline estimate
 * is "~20s average per level". Retuned 2026-09-16 so 3★ actually requires quick
 * play instead of being trivial at a leisurely pace: ~21-25s for the smallest
 * boards, topping out around ~50s for the biggest 8x8/10-pair expert levels.
 */
const PAR_TIME_BASE_SEC = 8;
const PAR_TIME_PER_CELL_SEC = 0.35;
const PAR_TIME_PER_PAIR_SEC = 2;

export function getParTimeSec(size: number, pairCount: number): number {
  return Math.round(
    PAR_TIME_BASE_SEC + size * size * PAR_TIME_PER_CELL_SEC + pairCount * PAR_TIME_PER_PAIR_SEC,
  );
}

/**
 * Star formula from doc/07-levels-data.md §7.7: 3★ with no reveal hints AND
 * finishing within par time, 2★ with exactly one hint OR a clean-but-slow solve,
 * 1★ for two or more hints or more than 10 "corrections" (undos plus redraws —
 * whichever rule fires first drags the score down to its floor — a hint-heavy
 * fast solve still caps at 1-2). `redrawCount` (added 2026-09-19, user: "minus
 * points if you rework an already-connected line") counts starting a fresh drag
 * from an already-fully-connected pair's endpoint, which silently wipes the
 * finished path — previously free, now weighted the same as an explicit undo
 * since both represent the player throwing away a completed connection.
 */
export function calculateStars(
  hintsUsed: number,
  undoCount: number,
  redrawCount: number,
  timeSec: number,
  parTimeSec: number,
): number {
  if (hintsUsed >= 2 || undoCount + redrawCount > 10) return 1;
  if (hintsUsed === 1) return 2;
  return timeSec <= parTimeSec ? 3 : 2;
}

export function markLevelCompleted(
  levelId: string,
  hintsUsed: number,
  undoCount: number,
  redrawCount: number,
  timeSec: number,
  parTimeSec: number,
): void {
  const progress = loadProgress();
  const stars = calculateStars(hintsUsed, undoCount, redrawCount, timeSec, parTimeSec);
  const previousStars = progress.completed[levelId]?.stars ?? 0;

  progress.completed[levelId] = {
    stars: Math.max(stars, previousStars),
    hints: hintsUsed,
    timeSec,
    at: new Date().toISOString(),
  };
  progress.lastLevel = levelId;
  saveProgress(progress);
}

export function setLastLevel(levelId: string): void {
  const progress = loadProgress();
  progress.lastLevel = levelId;
  saveProgress(progress);
}

export function getCompletedLevelIds(): Set<string> {
  return new Set(Object.keys(loadProgress().completed));
}

export function getLastLevelId(): string {
  return loadProgress().lastLevel;
}
